import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import {
  Box,
  Button,
  Container,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Snackbar,
  Popover,
  MenuItem,
  InputAdornment,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Flag,
  Settings,
  Search,
  FilterList,
} from "@mui/icons-material";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const assetFields = [
  "bo_id",
  "asset_det_id",
  "asset_id",
  "description",
  "purchase_at",
  "manufacturer",
  "model_no",
  "model_name",
  "sprice",
  "purchase_date",
  "warranty_number",
  "warranty_validity",
  "warranty_status",
  "service_date",
  "service_by",
  "installation_flag",
  "installation_cost",
  "qty",
  "currency",
  "status",
  "asset_condition",
  "replaced_on",
  "replaced_by",
  "replace_reason",
];

const dbDropdownFields = ["bo_id"];
const popupFields = assetFields;
const readOnlyFields = ["asset_id", "description"];

const fieldLabels = {};
assetFields.forEach((field) => {
  fieldLabels[field] = field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
});

const AssetsDetails = ({ asset_id }) => {
  const [assets, setAssets] = useState([]);
  const [filters, setFilters] = useState({});
  const [anchorEls, setAnchorEls] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const initial = {};
    assetFields.forEach((f) => (initial[f] = true));
    return initial;
  });
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [newAsset, setNewAsset] = useState(initNewAsset());
  const [editAsset, setEditAsset] = useState({});
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownOptions, setDropdownOptions] = useState({
    bo_id: [],
    boToAssetMap: {},
    assetToDescMap: {},
  });

  const rowsPerPage = 10;

  function initNewAsset() {
    return {
      delete_flag: "N",
      data_source: "UI",
      created_by: "system",
      created_on_date: new Date().toISOString().split("T")[0],
    };
  }

  const fetchAssets = useCallback(async () => {
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
        search: search.trim(),
        asset_id,
      };
      const res = await axios.get(
        `${BASE_URL}/assetsdetails/paginated`,
        { params }
      );
      setAssets(res.data.data);
      setTotalCount(res.data.total);
    } catch (err) {
      console.error("Error fetching assets:", err);
    }
  }, [page, filters, search, asset_id]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/assetsdetails/asset-dropdown-options`)
      .then((res) => {
        const boSet = new Set();
        const boToAssetMap = {};
        const assetToDescMap = {};

        res.data.forEach((row) => {
          if (row.bo_id) boSet.add(row.bo_id);
          if (row.bo_id && row.asset_id) boToAssetMap[row.bo_id] = row.asset_id;
          if (row.asset_id && row.asset_description)
            assetToDescMap[row.asset_id] = row.asset_description;
        });

        setDropdownOptions({
          bo_id: [...boSet],
          boToAssetMap,
          assetToDescMap,
        });
      });
  }, []);

  const renderField = (field, state, setState) => {
    const isDropdown = dbDropdownFields.includes(field);
    const isReadOnly = readOnlyFields.includes(field);
    const options = isDropdown ? dropdownOptions[field] : null;

    const handleChange = (e) => {
      const value = e.target.value;

      if (field === "bo_id") {
        const assetId = dropdownOptions.boToAssetMap[value] || "";
        const desc = dropdownOptions.assetToDescMap[assetId] || "";
        setState((prev) => ({
          ...prev,
          bo_id: value,
          asset_id: assetId,
          description: desc,
        }));
      } else {
        setState((prev) => ({ ...prev, [field]: value }));
      }
    };

    return (
      <TextField
        key={field}
        select={isDropdown}
        label={fieldLabels[field] || field}
        value={state[field] || ""}
        onChange={handleChange}
        fullWidth
        size="small"
        InputProps={{ readOnly: isReadOnly && !isDropdown }}
      >
        {isDropdown &&
          options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
      </TextField>
    );
  };

  const handleAdd = async () => {
    try {
      await axios.post(`${BASE_URL}/assetsdetails`, newAsset);
      setOpen(false);
      setNewAsset(initNewAsset());
      setSnackbarMsg("Asset added");
      setShowSnackbar(true);
      fetchAssets();
    } catch (err) {
      console.error("Add failed:", err);
      setSnackbarMsg("Failed to add asset");
      setShowSnackbar(true);
    }
  };

  const handleEdit = async () => {
    await axios.put(
      `${BASE_URL}/assetsdetails/${editAsset.asset_det_id}`,
      editAsset
    );
    setEditOpen(false);
    setSnackbarMsg("Asset updated");
    setShowSnackbar(true);
    fetchAssets();
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/assetsdetails/${id}`);
      setSnackbarMsg("Asset deleted");
      setShowSnackbar(true);
      fetchAssets();
    } catch (err) {
      setSnackbarMsg("Failed to delete asset");
      setShowSnackbar(true);
    }
  };

  const handleToggleFlag = async (id) => {
    try {
      await axios.patch(
        `${BASE_URL}/assetsdetails/${id}/toggle-flag`
      );
      setSnackbarMsg("Warranty flag toggled");
      setShowSnackbar(true);
      fetchAssets();
    } catch (err) {
      setSnackbarMsg("Failed to toggle warranty flag");
      setShowSnackbar(true);
    }
  };

  return (
    <ThemeProvider theme={Table_theme}>
      <Box
        sx={{ display: "flex", flexDirection: "column", pl: 2, pr: 2, pt: 2 }}
      >
        <Typography
          variant="h5"
          gutterBottom
          color="#454545"
          fontSize="20px"
          fontWeight="600"
        >
          Assets Details
        </Typography>
        <Box display="flex" gap={2} alignItems="center" mt={2} mb={2}>
          <IconButton onClick={(e) => setColumnAnchorEl(e.currentTarget)}>
            <Settings fontSize="small" />
          </IconButton>
          <Popover
            open={Boolean(columnAnchorEl)}
            anchorEl={columnAnchorEl}
            onClose={() => setColumnAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box
              p={2}
              maxHeight={400}
              overflow="auto"
              display="flex"
              flexDirection="column"
              gap={1}
            >
              {assetFields.map((field) => (
                <FormControlLabel
                  key={field}
                  control={
                    <Switch
                      checked={columnVisibility[field]}
                      onChange={() =>
                        setColumnVisibility((prev) => ({
                          ...prev,
                          [field]: !prev[field],
                        }))
                      }
                    />
                  }
                  label={fieldLabels[field]}
                />
              ))}
            </Box>
          </Popover>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
          >
            Add
          </Button>
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: "0.7rem" }}>
                  <strong>Actions</strong>
                </TableCell>
                {assetFields
                  .filter((f) => columnVisibility[f])
                  .map((field) => (
                    <TableCell
                      key={field}
                      sx={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}
                    >
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <strong>{fieldLabels[field]}</strong>
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            setAnchorEls((prev) => ({
                              ...prev,
                              [field]: e.currentTarget,
                            }))
                          }
                        >
                          <FilterList fontSize="small" />
                        </IconButton>
                      </Box>
                      <Popover
                        open={Boolean(anchorEls[field])}
                        anchorEl={anchorEls[field]}
                        onClose={() =>
                          setAnchorEls((prev) => ({ ...prev, [field]: null }))
                        }
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                      >
                        <Box
                          p={2}
                          display="flex"
                          flexDirection="column"
                          gap={1}
                          width={200}
                        >
                          <TextField
                            size="small"
                            fullWidth
                            placeholder={`Filter ${fieldLabels[field]}`}
                            value={filters[field] || ""}
                            onChange={(e) =>
                              setFilters((prev) => ({
                                ...prev,
                                [field]: e.target.value,
                              }))
                            }
                          />
                          <Box display="flex" justifyContent="space-between">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => {
                                setAnchorEls((prev) => ({
                                  ...prev,
                                  [field]: null,
                                }));
                                setPage(0);
                                fetchAssets();
                              }}
                            >
                              Apply
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setFilters((prev) => ({
                                  ...prev,
                                  [field]: "",
                                }));
                                setAnchorEls((prev) => ({
                                  ...prev,
                                  [field]: null,
                                }));
                                setPage(0);
                                fetchAssets();
                              }}
                            >
                              Clear
                            </Button>
                          </Box>
                        </Box>
                      </Popover>
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {assets.map((row, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontSize: "0.7rem" }}>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditAsset(row);
                          setEditOpen(true);
                        }}
                      >
                        <Edit fontSize="small" color="primary" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.asset_det_id)}
                      >
                        <Delete fontSize="small" sx={{ color: "red" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleFlag(row.asset_det_id)}
                      >
                        <Flag
                          fontSize="small"
                          sx={{
                            color:
                              row.warranty_status === "Valid"
                                ? "green"
                                : "orange",
                          }}
                        />
                      </IconButton>
                    </Box>
                  </TableCell>
                  {assetFields
                    .filter((f) => columnVisibility[f])
                    .map((field) => (
                      <TableCell
                        key={field}
                        sx={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}
                      >
                        {row[field]}
                      </TableCell>
                    ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <ThemeProvider theme={FormThemes}>
          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Add Asset</DialogTitle>
            <DialogContent sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {popupFields.map((field) =>
                renderField(field, newAsset, setNewAsset)
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleAdd} variant="contained">
                Add
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
        <ThemeProvider theme={FormThemes}>
          <Dialog
            open={editOpen}
            onClose={() => setEditOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Update Asset</DialogTitle>
            <DialogContent sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {popupFields.map((field) =>
                renderField(field, editAsset, setEditAsset)
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={handleEdit} variant="contained">
                Update
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSnackbar(false)}
          message={snackbarMsg}
        />
      </Box>
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page + 1} // Add +1 to match MUI pagination (1-based)
          onChange={(_, newPage) => setPage(newPage - 1)} // Subtract 1 to set back to 0-based index
          color="primary"
          variant="outlined"
          shape="rounded"
          sx={{
            "& .MuiPaginationItem-root": {
              borderRadius: "8px",
              minWidth: "32px",
              height: "32px",
              fontSize: "14px",
            },
            "& .Mui-selected": {
              backgroundColor: "#b3dbea !important",
              color: "black",
              fontWeight: "bold",
            },
            "& .MuiPaginationItem-ellipsis": {
              display: "none",
            },
          }}
        />
      </Box>
    </ThemeProvider>
  );
};

export default AssetsDetails;
